import {
  Grid,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
} from "@mui/material";
import { useEffect, useState } from "react";

interface PastAmount {
  date: string;
  amount: number;
}

interface Difference {
  status: boolean;
  differenceAmount: number;
  valueSet: number;
}

interface CollectionsBreakdownProps {
  onDifferenceChange?: (diff: Difference) => void;
  onValueChange: (value: any) => void;
  calculationAdvisor: Record<string, number>;
  calculationAdvisorTitles: Record<string, string>;
  initialValue?: any;
  amountsHistory?: Record<string, { details?: { amountEntered?: number } }>;
  cancelDigitNumber?: boolean;
}

const CollectionsBreakdown = ({
  onDifferenceChange = () => {},
  onValueChange,
  calculationAdvisor,
  calculationAdvisorTitles,
  initialValue = 0,
  amountsHistory,
  cancelDigitNumber = false,
}: CollectionsBreakdownProps) => {
  const [valueSet, setValueSet] = useState(initialValue);
  const [difference, setDifference] = useState<Difference>({
    status: false,
    differenceAmount: 0,
    valueSet: 0,
  });
  const [pastAmounts, setPastAmounts] = useState<PastAmount[] | null>(null);
  const [totalPastAmounts, setTotalPastAmounts] = useState<number>(0);

  // Notifica cambios en la diferencia
  useEffect(() => {
    onDifferenceChange(difference);
  }, [difference, onDifferenceChange]);

  // Reinicia el valor si cancelDigitNumber es true
  useEffect(() => {
    if (cancelDigitNumber && amountsHistory) setValueSet(0);
  }, [amountsHistory, cancelDigitNumber]);

  // Cálculo principal de diferencias
  useEffect(() => {
    if (amountsHistory && typeof amountsHistory === "object") {
      const arr: PastAmount[] = Object.entries(amountsHistory).map(
        ([date, item]: [string, any]) => ({
          date,
          amount: item.details?.amountEntered ?? 0,
        })
      );

      const total = arr.reduce((sum, curr) => sum + curr.amount, 0);
      const systemTotal =
        (calculationAdvisor.cobrosTPV ?? 0) +
        (calculationAdvisor.cobrosMultiPagos ?? 0);

      const diffAmount = systemTotal - total - valueSet;

      setDifference({
        status: diffAmount !== 0,
        differenceAmount: diffAmount,
        valueSet: valueSet,
      });

      setTotalPastAmounts(total);
      setPastAmounts(arr);
    }
  }, [amountsHistory, calculationAdvisor, valueSet]);

  return (
    <>
      <Typography variant="h6" gutterBottom>
        Resumen de Cobros del Asesor
      </Typography>
      <List dense>
        {Object.entries(calculationAdvisor).map(([key, value]) => (
          <ListItem key={key} divider>
            <ListItemText
              sx={{ margin: 0, padding: 0 }}
              primary={
                <Grid
                  container
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Grid item xs>
                    <Typography variant="body2" fontWeight="bold" align="left">
                      {calculationAdvisorTitles[key]}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="body2" color="text.secondary">
                      ${value}
                    </Typography>
                  </Grid>
                </Grid>
              }
            />
          </ListItem>
        ))}

        {/* Diferencia entre sistema (TPV + MultiPagos) y montos ingresados */}
        <ListItem key={"difference"} divider>
          <ListItemText
            sx={{ margin: 0, padding: 0 }}
            primary={
              <Grid
                container
                justifyContent="space-between"
                alignItems="center"
              >
                <Grid item xs>
                  <Typography variant="body2" fontWeight="bold" align="left">
                    Diferencia entre sistema y lote
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body2" color="text.secondary">
                    $
                    {(calculationAdvisor.cobrosTPV ?? 0) +
                      (calculationAdvisor.cobrosMultiPagos ?? 0) -
                      totalPastAmounts -
                      valueSet}
                  </Typography>
                </Grid>
              </Grid>
            }
          />
        </ListItem>

        <ListSubheader disableSticky sx={{ m: 0, p: 0, pt: 1 }}>
          <Typography variant="body2" fontWeight="bold">
            Historial de montos
          </Typography>
        </ListSubheader>

        {/* Si no hay historial, muestra el monto actual */}
        {!pastAmounts && (
          <ListItem key={"seted_amount"} divider>
            <ListItemText
              sx={{ m: 0, p: 0 }}
              primary={
                <Grid
                  container
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Grid item xs>
                    <Typography variant="body2" align="left">
                      Monto ingresado
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="body2" color="text.secondary">
                      ${valueSet}
                    </Typography>
                  </Grid>
                </Grid>
              }
            />
          </ListItem>
        )}

        {/* Historial */}
        {pastAmounts?.map(({ date, amount }) => (
          <ListItem key={date} divider>
            <ListItemText
              sx={{ m: 0, p: 0 }}
              primary={
                <Grid
                  container
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Grid item xs>
                    <Typography variant="body2" align="left">
                      {date}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="body2" color="text.secondary">
                      ${amount}
                    </Typography>
                  </Grid>
                </Grid>
              }
            />
          </ListItem>
        ))}

        {/* Campo de ingreso manual */}
        {!cancelDigitNumber && (
          <ListItem key={"valueSet"} divider>
            <ListItemText
              sx={{ margin: 0, padding: 0 }}
              primary={
                <Grid
                  container
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Grid item xs>
                    <Typography variant="body2" fontWeight="bold" align="left">
                      Total de cierre de lote
                    </Typography>
                  </Grid>
                  <Grid item>
                    <TextField
                      label="Ingrese el valor del cierre de lote"
                      variant="outlined"
                      fullWidth
                      value={valueSet}
                      type="number"
                      disabled={!!initialValue}
                      onChange={(e) => {
                        const { value } = e.target;

                        if (value === "") {
                          setValueSet("");
                          onValueChange("");
                          return;
                        }

                        const newValue = Number(value);
                        setValueSet(newValue);
                        onValueChange(newValue);
                      }}
                      sx={{ mb: 2 }}
                    />
                  </Grid>
                </Grid>
              }
            />
          </ListItem>
        )}
      </List>
    </>
  );
};

export default CollectionsBreakdown;
